import {
  htFirmaTecnico,
  htIcpIti,
  htSelloDigital,
  HT_FIRMA_TECNICO_ASPECT,
  HT_ICP_ITI_ASPECT,
  HT_SELLO_DIGITAL_ASPECT,
} from './htSheetImages';
import { htPdfDrawFullWidthBlockFrame } from './htPdfFullWidthFrame';
import { FONT_SM, PAD, rowHeightMm } from './htPdfRowDraw';
import { formatHtTreatmentDateDisplay } from './htDisplayFormat';

const FOOTER_ROW_PT = {
  54: 11.5,
  55: 11.5,
  56: 13.8,
  57: 14.4,
  58: 14.4,
  59: 14.4,
  60: 8,
  61: 14.4,
  62: 14.4,
  63: 8,
  64: 21,
  65: 16,
};

const FONT_DIGITAL = 5.35;
const FONT_DIGITAL_SIGNED = 5.1;
const LINE_ISSUE = 2.85;
const LINE_DIGITAL = 2.35;
const LINE_DIGITAL_SIGNED = 2.05;
const TECH_LINE_GAP_ABOVE = 0.72;
const TECH_LINE_GAP_BELOW = 0.42;
const TECH_SIGN_LINE_STEP = 3.35;
/** Desplazamiento del bloque Local de Emissão / Data en el pie HT (mm). */
const ISSUE_TEXT_SHIFT_MM = 2.2;
/** Bloque ICP/ITI un poco a la izquierda dentro de su columna (mm). */
const ICP_BLOCK_SHIFT_MM = 4.2;

function blockHeightMm(rows, ptMap) {
  return rows.reduce((sum, r) => sum + rowHeightMm(r, ptMap), 0);
}

function drawTextLines(doc, x, y, w, h, lines, lineStep = LINE_ISSUE, fontSize = FONT_SM) {
  let ty = y + 1.8;
  const maxY = y + h - 1.5;
  lines.forEach(({ text, bold }) => {
    if (ty > maxY) return;
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    doc.text(text, x + PAD, ty, { baseline: 'top', maxWidth: Math.max(1, w - PAD * 2) });
    ty += lineStep;
  });
}

function drawTextLinesVerticallyCentered(
  doc,
  x,
  y,
  w,
  h,
  lines,
  lineStep = LINE_ISSUE,
  fontSize = FONT_SM
) {
  const contentH = Math.max(0, lines.length * lineStep - 0.35);
  let ty = y + Math.max(PAD, (h - contentH) / 2);
  const maxY = y + h - PAD;
  lines.forEach(({ text, bold }) => {
    if (ty > maxY) return;
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    doc.text(text, x + PAD, ty, { baseline: 'top', maxWidth: Math.max(1, w - PAD * 2) });
    ty += lineStep;
  });
}

function fitImageSize(boxW, boxH, aspect) {
  let imgW = boxW;
  let imgH = imgW / aspect;
  if (imgH > boxH) {
    imgH = boxH;
    imgW = imgH * aspect;
  }
  return { imgW, imgH };
}

/** Escala imagen dentro del rectángulo manteniendo proporción (centrada). */
function drawImageFitAspect(doc, img, boxX, boxY, boxW, boxH, aspect) {
  const { imgW, imgH } = fitImageSize(boxW, boxH, aspect);
  const ix = boxX + (boxW - imgW) / 2;
  const iy = boxY + (boxH - imgH) / 2;
  doc.addImage(img, 'PNG', ix, iy, imgW, imgH);
  return { imgW, imgH, ix, iy };
}

/** Centro 56–63: una sola franja horizontal (plantilla Invoice). */
function drawHtFooterCenterBand(doc, S, xCenter, wCenter, blockTop, hSignBlock) {
  const bandInset = 0.55;
  const bandH = hSignBlock - bandInset * 2;
  const bandTop = blockTop + bandInset;
  const gap = 0.85;

  const wFirma = Math.min(14.5, wCenter * 0.12);
  const wSello = Math.min(19, wCenter * 0.155);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_DIGITAL);
  const signatoryLines = S.HT_DIGITAL_SIGNATORY_BLOCK.split('\n');
  const namesNeedW =
    Math.max(...signatoryLines.map((line) => doc.getTextWidth(line)), 0) + 0.8;
  const wNames = Math.max(wCenter * 0.145, namesNeedW);
  const wSigned = Math.min(22, wCenter * 0.11);
  const bandTotalW = wFirma + gap + wNames + gap + wSello + gap + wSigned;
  const xFirma = xCenter + PAD * 0.35;
  const xNames = xFirma + wFirma + gap;
  const xSello = xNames + wNames + gap;
  const xSigned = xSello + wSello + gap;

  const signTextH = signatoryLines.length * LINE_DIGITAL;
  const namesTop = bandTop + (bandH - signTextH) / 2;
  const namesBottom = namesTop + signTextH;

  const firmaBoxH = Math.min(bandH * 0.52, signTextH + 1.5);
  const firmaBoxY = namesTop + (signTextH - firmaBoxH) / 2;
  const { iy: firmaIy, imgH: firmaImgH } = drawImageFitAspect(
    doc,
    htFirmaTecnico,
    xFirma,
    firmaBoxY,
    wFirma,
    firmaBoxH,
    HT_FIRMA_TECNICO_ASPECT
  );

  const signedLines = S.HT_DIGITAL_SIGNED_BY.split('\n');
  const signedBlockH = signedLines.length * LINE_DIGITAL_SIGNED;
  const signedTop = bandTop + (bandH - signedBlockH) / 2;

  const selloBoxH = bandH * 0.9;
  const selloBoxY = signedTop + (signedBlockH - selloBoxH) / 2;
  const { iy: selloIy, imgH: selloImgH, imgW: selloImgW } = drawImageFitAspect(
    doc,
    htSelloDigital,
    xSello,
    selloBoxY,
    wSello,
    selloBoxH,
    HT_SELLO_DIGITAL_ASPECT
  );
  const xSelloEnd = xSello + selloImgW;

  let dy = namesTop;
  signatoryLines.forEach((line) => {
    doc.text(line, xNames, dy, { baseline: 'top' });
    dy += LINE_DIGITAL;
  });

  doc.setFontSize(FONT_DIGITAL_SIGNED);
  let sy = signedTop;
  let signedBottom = signedTop;
  let signedRight = xSigned;
  signedLines.forEach((line) => {
    doc.text(line, xSigned, sy, { baseline: 'top' });
    signedBottom = sy + LINE_DIGITAL_SIGNED;
    signedRight = Math.max(signedRight, xSigned + doc.getTextWidth(line));
    sy += LINE_DIGITAL_SIGNED;
  });

  const sigBottom = Math.max(
    firmaIy + firmaImgH,
    selloIy + selloImgH,
    namesBottom,
    signedBottom
  );
  const lineLeft = xFirma;
  const lineRight = Math.max(xFirma + bandTotalW, xSelloEnd, signedRight + 0.5);
  return {
    sigBottom,
    lineLeft,
    lineRight,
    techCx: (lineLeft + lineRight) / 2,
  };
}

/** Filas 54–65: espacio tras marcas, firmas y emisión. */
export function drawHtFooterPdf(doc, params, xs, y) {
  const S = params.static;
  const xEnd = xs[5];
  const xContent = xs[0];
  const wContent = xEnd - xContent;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_SM);

  const hGap = blockHeightMm([54, 55], FOOTER_ROW_PT);
  htPdfDrawFullWidthBlockFrame(doc, xs, y, hGap, 54, 55);
  y += hGap;

  const hSignBlock = blockHeightMm([56, 57, 58, 59, 60, 61, 62, 63], FOOTER_ROW_PT);
  const blockTop = y;
  htPdfDrawFullWidthBlockFrame(doc, xs, y, hSignBlock, 56, 63);

  const wIssue = wContent * 0.24;
  const wCenter = wContent * 0.52;
  const wIcp = wContent * 0.24;
  const xIssue = xContent;
  const xCenter = xIssue + wIssue;
  const xIcp = xCenter + wCenter;

  const issueLines = [
    { text: S.HT_ISSUE_PLACE_LABEL, bold: true },
    { text: S.HT_ISSUE_PLACE_EN, bold: false },
    { text: S.HT_CITY_ISSUE.trim(), bold: false },
    { text: S.HT_DATE_LABEL, bold: false },
    {
      text: formatHtTreatmentDateDisplay(params.certificateIssueDateIso),
      bold: false,
    },
  ];
  drawTextLinesVerticallyCentered(
    doc,
    xIssue + ISSUE_TEXT_SHIFT_MM,
    blockTop,
    wIssue - ISSUE_TEXT_SHIFT_MM,
    hSignBlock,
    issueLines
  );

  const sigLayout = drawHtFooterCenterBand(doc, S, xCenter, wCenter, blockTop, hSignBlock);

  const valLines = S.HT_ICP_VALIDATION_LINES.map((text) => ({ text, bold: false }));
  const valTextH = 7.2;
  const xIcpDraw = xIcp - ICP_BLOCK_SHIFT_MM;
  drawTextLines(doc, xIcpDraw, blockTop, wIcp, valTextH, valLines, 3.1, FONT_SM);

  const icpAreaY = blockTop + valTextH + 0.8;
  const icpAreaH = hSignBlock - valTextH - 1.5;
  drawImageFitAspect(
    doc,
    htIcpIti,
    xIcpDraw + PAD * 0.35,
    icpAreaY,
    wIcp - PAD * 0.5,
    icpAreaH,
    HT_ICP_ITI_ASPECT
  );

  const techLineY = sigLayout.sigBottom + TECH_LINE_GAP_ABOVE;
  doc.setLineWidth(0.15);
  doc.line(sigLayout.lineLeft, techLineY, sigLayout.lineRight, techLineY);

  y += hSignBlock;

  const h64 = rowHeightMm(64, FOOTER_ROW_PT);
  const h65 = rowHeightMm(65, FOOTER_ROW_PT);
  htPdfDrawFullWidthBlockFrame(doc, xs, y, h64 + h65, 64, 65);

  const techCx = sigLayout.techCx;
  const techTextY = techLineY + TECH_LINE_GAP_BELOW;
  doc.setFontSize(FONT_SM);
  doc.text(S.HT_TECH_SIGN, techCx, techTextY, {
    align: 'center',
    baseline: 'top',
    maxWidth: wCenter * 0.88,
  });
  doc.text(S.HT_TECH_SIGN_EN, techCx, techTextY + TECH_SIGN_LINE_STEP, {
    align: 'center',
    baseline: 'top',
    maxWidth: wCenter * 0.85,
  });

  return y + h64 + h65;
}
